/**
 * Helper function to get translations outside of React components
 * This is useful for hooks and service functions that need translations
 * but can't use the useTranslation hook
 */

import {resolveTranslation} from './translation.functions'
import {LOCAL_TRANSLATIONS, DEFAULT_LANGUAGE} from '../i18n/localDictionary'

const translationCache: Record<string, Record<string, string>> = {}

/**
 * Get translation by key with optional parameters for interpolation.
 * Prefers the API dictionary cache, then the bundled local dictionary, then
 * returns the raw key.
 * @param key - Translation key (e.g., 'activity:dayApprovedSuccess')
 * @param params - Optional array of parameters for interpolation
 * @returns Translated string or the key if translation not found
 */
export function getTranslation(
  key: string,
  params?: (string | null)[],
): string {
  const language = window.navigator.language.slice(0, 2) || DEFAULT_LANGUAGE
  const primary = translationCache[language] ?? {}
  const fallback =
    LOCAL_TRANSLATIONS[language] ?? LOCAL_TRANSLATIONS[DEFAULT_LANGUAGE]

  return resolveTranslation(key, primary, fallback, params)
}

/**
 * Set translations cache (called by useTranslation)
 * @param language - Language code
 * @param translations - Translation object
 */
export function setTranslationCache(
  language: string,
  translations: Record<string, string>,
) {
  translationCache[language] = translations
}

/**
 * Get current translations cache
 * @param language - Language code
 * @returns Translation object
 */
export function getTranslationCache(language: string) {
  return translationCache[language]
}
